import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import TimeField from '../../../components/atoms/forms/TimeField';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import Input from '../../../components/atoms/forms/Input';
import Textarea from '../../../components/atoms/forms/Textarea';
import ChecklistSection from './ChecklistSection';
import RecommendedActionCallout from './RecommendedActionCallout';
import useChecklistSection from './useChecklistSection';
import useSupervisorAction from './useSupervisorAction';
import {
  REVIEW_PHASE_DAY_OF,
  REVIEW_PHASE_PREP,
  computeDelayMinutes,
  formatDelayMinutes,
} from './utils';

export const EVENT_DEPARTURE = 'departure';
export const EVENT_ARRIVAL = 'arrival';

// Per-event configuration: same UI shape, different labels + storage key.
// Departure is captured during Prep (the truck leaves origin the day before).
// Arrival is captured during Day Of (the truck arrives at the facility).
const EVENT_CONFIG = {
  [EVENT_DEPARTURE]: {
    title: 'Line Haul Departure Status',
    description:
      'Confirm line haul departure from origin — did it depart on time?',
    statusQuestion: 'Did line haul depart on time?',
    expectedLabel: 'Expected Departure Time',
    estimatedLabel: 'Estimated Departure Time from Origin',
    sectionId: 'line-haul-departure',
  },
  [EVENT_ARRIVAL]: {
    title: 'Line Haul Arrival Status',
    description:
      'Confirm line haul arrival at the facility — did it arrive on time?',
    statusQuestion: 'Did line haul arrive on time?',
    expectedLabel: 'Original Arrival Time',
    estimatedLabel: 'Updated Arrival Time at Facility',
    sectionId: 'line-haul-arrival',
  },
};

const STATUS_OPTIONS = [
  { value: 'on-time', label: 'On time' },
  { value: 'delayed', label: 'Delayed' },
];

// Supervisors edit these manually since the backend has no
// expectedDepartureTime / expectedArrivalTime field today.
const INITIAL_FIELDS = {
  status: '',
  expectedTime: '06:45',
  estimatedTime: '',
  routesGoOutTime: '08:00',
  notes: '',
};

const StyledDiv = styledComponent('div', {
  '& .lh-field': {
    marginBottom: theme.spacing.fieldGap,
  },
  // First form field needs breathing room below ChecklistSection's description.
  '& .lh-field:first-child, & .lh-field-row:first-child': {
    marginTop: theme.spacing.fieldGap,
  },
  '& .lh-field-row': {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.fieldGap,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .lh-label': {
    display: 'block',
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .lh-impact-assessment': {
    background: theme.colors.warningBg,
    borderLeft: `4px solid ${theme.colors.warning}`,
    padding: theme.spacing.bodyPadding,
    borderRadius: theme.radii.input,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .lh-impact-title': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.cautionText,
    marginBottom: theme.spacing.fieldGap,
  },
});

// Maps the section's draft `fields` object to the BE-expected
// LineHaulArrival data shape. Status is passed through as the FE-native
// value (`'on-time'`/`'delayed'`); BE's validator only checks field
// presence, not the value. Stops impacted is not tracked in the FE today —
// BE treats it as optional with a soft warning.
function buildLineHaulData(fields, computedDelayMinutes) {
  return {
    status: fields.status,
    expectedArrivalTime: fields.expectedTime,
    estimatedArrivalTime: fields.estimatedTime,
    calculatedDelayMinutes: computedDelayMinutes,
    notes: fields.notes || '',
  };
}

// Inverse of buildLineHaulData: maps a stored LineHaulArrival `data` object
// back into the section's draft fields. `routesGoOutTime` isn't persisted by
// the BE, so it falls back to its initial default via the draft merge.
export function parseLineHaulData(data) {
  if (!data || Object.keys(data).length === 0) return null;
  return {
    status: data.status || '',
    expectedTime: data.expectedArrivalTime || '',
    estimatedTime: data.estimatedArrivalTime || '',
    notes: data.notes || '',
  };
}

function LineHaulSection({
  event,
  stockingDate,
  fulfillmentCenter,
  market,
  stepNumber,
  serverFields,
  hasServerAction,
  onConsolidateRoutes,
  onCompletedChange,
}) {
  const config = EVENT_CONFIG[event];

  const { fields, setField, markComplete, cancel, completed } =
    useChecklistSection({
      sectionId: config.sectionId,
      stockingDate,
      initialFields: INITIAL_FIELDS,
      serverFields,
      hasServerAction,
      onCompletedChange,
    });

  const reviewPhase =
    event === EVENT_ARRIVAL ? REVIEW_PHASE_DAY_OF : REVIEW_PHASE_PREP;
  const supervisorAction = useSupervisorAction({
    fulfillmentCenter,
    market,
    reviewDate: stockingDate,
    reviewPhase,
  });

  const delayMinutes = useMemo(
    () => computeDelayMinutes(fields.expectedTime, fields.estimatedTime),
    [fields.expectedTime, fields.estimatedTime],
  );

  const handleMarkComplete = useCallback(() => {
    markComplete();
    supervisorAction.submit({
      eventType: 'LineHaulArrival',
      data: buildLineHaulData(fields, delayMinutes),
    });
  }, [markComplete, supervisorAction, fields, delayMinutes]);

  const isDelayed = fields.status === 'delayed';
  // Impact Assessment only auto-expands once the computed delay exceeds
  // 30 min — minor delays don't warrant the recommended-action prompt.
  const showImpact = isDelayed && delayMinutes !== null && delayMinutes > 30;

  return (
    <StyledDiv data-testid={`line-haul-${event}`}>
      <ChecklistSection
        stepNumber={stepNumber}
        title={config.title}
        description={config.description}
        completed={completed}
        onCancel={cancel}
        onMarkComplete={handleMarkComplete}
      >
        <div className="lh-field">
          <label className="lh-label" htmlFor={`lh-status-${event}`}>
            {config.statusQuestion}
          </label>
          <SingleSelect
            inputId={`lh-status-${event}`}
            options={STATUS_OPTIONS}
            value={
              STATUS_OPTIONS.find((o) => o.value === fields.status) || null
            }
            onChange={(opt) => setField('status', opt ? opt.value : '')}
            placeholder="Select…"
            isClearable
            data-testid="lh-status"
          />
        </div>

        <div className="lh-field-row">
          <div>
            <label className="lh-label" htmlFor={`lh-expected-${event}`}>
              {config.expectedLabel}
            </label>
            <TimeField
              id={`lh-expected-${event}`}
              value={fields.expectedTime}
              onChange={(v) => setField('expectedTime', v)}
              data-testid="lh-expected"
            />
          </div>
          <div>
            <label className="lh-label" htmlFor={`lh-routes-go-out-${event}`}>
              What time do you expect routes to go out?
            </label>
            <TimeField
              id={`lh-routes-go-out-${event}`}
              value={fields.routesGoOutTime}
              onChange={(v) => setField('routesGoOutTime', v)}
              data-testid="lh-routes-go-out"
            />
          </div>
        </div>

        {isDelayed && (
          <div
            className="lh-impact-assessment"
            data-testid="lh-impact-assessment"
          >
            <div className="lh-impact-title">⚠️ Delay Impact Assessment</div>
            <div className="lh-field-row">
              <div>
                <label className="lh-label" htmlFor={`lh-estimated-${event}`}>
                  {config.estimatedLabel}
                </label>
                <TimeField
                  id={`lh-estimated-${event}`}
                  value={fields.estimatedTime}
                  onChange={(v) => setField('estimatedTime', v)}
                  includeEmpty
                  data-testid="lh-estimated"
                />
              </div>
              <div>
                <label className="lh-label" htmlFor={`lh-delay-${event}`}>
                  Calculated Delay
                </label>
                <Input
                  id={`lh-delay-${event}`}
                  value={formatDelayMinutes(delayMinutes)}
                  readOnly
                  dataTestId="lh-delay"
                />
              </div>
            </div>
          </div>
        )}

        {showImpact && (
          <RecommendedActionCallout
            title="⚠️ Recommended action"
            body="Based on the delay, you may need to adjust delivery windows,
              consolidate impacted routes, or exclude routes. Make these
              changes in the Route Editor."
            ctaLabel="Go to Route Editor →"
            onCta={onConsolidateRoutes}
            testId="lh-action"
            ctaTestId="lh-route-editor-cta"
          />
        )}

        <div className="lh-field">
          <label className="lh-label" htmlFor={`lh-notes-${event}`}>
            Notes (Optional)
          </label>
          <Textarea
            id={`lh-notes-${event}`}
            rows="2"
            placeholder={`Add notes about line haul ${event} status...`}
            value={fields.notes}
            onChange={(e) => setField('notes', e.target.value)}
            dataTestId="lh-notes"
          />
        </div>
      </ChecklistSection>
    </StyledDiv>
  );
}

LineHaulSection.propTypes = {
  event: PropTypes.oneOf([EVENT_DEPARTURE, EVENT_ARRIVAL]).isRequired,
  stockingDate: PropTypes.string.isRequired,
  fulfillmentCenter: PropTypes.string.isRequired,
  market: PropTypes.string,
  stepNumber: PropTypes.number,
  serverFields: PropTypes.shape({
    status: PropTypes.string,
    expectedTime: PropTypes.string,
    estimatedTime: PropTypes.string,
    notes: PropTypes.string,
  }),
  hasServerAction: PropTypes.bool,
  onConsolidateRoutes: PropTypes.func,
  onCompletedChange: PropTypes.func,
};

LineHaulSection.defaultProps = {
  market: '',
  stepNumber: 1,
  serverFields: null,
  hasServerAction: false,
  onConsolidateRoutes: () => {},
  onCompletedChange: () => {},
};

export default LineHaulSection;
